const handles = new WeakMap()

function isJQuery(target) {
    return !!(target && (target.jquery || (typeof target.get === 'function' && target.length !== undefined && target[0] && target[0].nodeType === 1)))
}

export function resolveFromTarget(target) {
    if (!target) return null
    if (typeof target === 'string') return document.querySelector(target)
    if (isJQuery(target)) return target[0] || null
    if (target.nodeType === 1) return target
    return null
}

function defaultBackdrop() {
    return true
}

function defaultKeyboard() {
    return true
}

export class ExistingModalHandle {
    constructor(el, options = {}) {
        this._el = el
        this._backdrop = options.backdrop !== undefined ? options.backdrop : defaultBackdrop()
        this._keyboard = options.keyboard !== undefined ? options.keyboard : defaultKeyboard()
        this._titleSelector = options.titleSelector || null
        this._bs = null
        this._listenersBound = false
        this._onShown = []
        this._onHidden = []
    }

    withBackdrop(backdrop) {
        if (this._bs) {
            console.warn('[GenericModal.from] withBackdrop() ignored after first show(); dispose() then from() again to change options.')
            return this
        }
        this._backdrop = backdrop
        return this
    }

    withKeyboard(keyboard) {
        if (this._bs) {
            console.warn('[GenericModal.from] withKeyboard() ignored after first show(); dispose() then from() again to change options.')
            return this
        }
        this._keyboard = keyboard
        return this
    }

    onShown(fn) {
        if (typeof fn === 'function') this._onShown.push(fn)
        this._bindLifecycle()
        return this
    }

    onHidden(fn) {
        if (typeof fn === 'function') this._onHidden.push(fn)
        this._bindLifecycle()
        return this
    }

    withTitle(text, selector) {
        const node = this._findTitleNode(selector)
        if (!node) {
            console.warn('[GenericModal.from] withTitle(): no title node found')
            return this
        }
        node.textContent = text == null ? '' : String(text)
        return this
    }

    _findTitleNode(selector) {
        const el = this._el
        const explicit = selector || this._titleSelector
        if (explicit) {
            return el.querySelector(explicit) || document.querySelector(explicit)
        }
        const id = el.id
        if (id) {
            const byId = document.getElementById(`${id}Title`)
            if (byId) return byId
        }
        return el.querySelector('[data-gm-title]')
            || el.querySelector('.modal-title-text')
            || el.querySelector('.modal-title')
    }

    _ensureBootstrap() {
        if (this._bs) return this._bs
        if (!window.bootstrap || !window.bootstrap.Modal) {
            throw new Error('[GenericModal.from] bootstrap.Modal is not available')
        }
        this._bs = new window.bootstrap.Modal(this._el, {
            backdrop: this._backdrop,
            keyboard: this._keyboard
        })
        this._bindLifecycle()
        return this._bs
    }

    _bindLifecycle() {
        if (this._listenersBound || !this._el) return
        this._listenersBound = true
        this._el.addEventListener('shown.bs.modal', () => {
            this._onShown.forEach(fn => {
                try { fn() } catch (e) { console.error(e) }
            })
        })
        this._el.addEventListener('hidden.bs.modal', () => {
            this._onHidden.forEach(fn => {
                try { fn() } catch (e) { console.error(e) }
            })
        })
    }

    show() {
        if (this._el.parentElement !== document.body) {
            document.body.appendChild(this._el)
        }
        this._ensureBootstrap().show()
        return this
    }

    hide() {
        if (this._bs) this._bs.hide()
        return this
    }

    toggle() {
        this._ensureBootstrap().toggle()
        return this
    }

    dispose() {
        if (this._bs) {
            this._bs.dispose()
            this._bs = null
        }
        handles.delete(this._el)
        return this
    }

    getElement() {
        return this._el
    }

    getInstance() {
        return this._bs
    }
}

export function fromExistingModal(target, options) {
    const el = resolveFromTarget(target)
    if (!el) {
        const label = typeof target === 'string' ? target : String(target)
        console.error(`[GenericModal.from] element not found: ${label}`)
        throw new Error(`[GenericModal.from] element not found: ${label}`)
    }

    const existing = handles.get(el)
    if (existing) {
        if (options && !existing._bs) {
            if (options.backdrop !== undefined) existing._backdrop = options.backdrop
            if (options.keyboard !== undefined) existing._keyboard = options.keyboard
            if (options.titleSelector) existing._titleSelector = options.titleSelector
        }
        return existing
    }

    const handle = new ExistingModalHandle(el, options || {})
    handles.set(el, handle)
    return handle
}
